/**
 * PublishToLessonModal.jsx
 * ────────────────────────
 * Beautiful Deskey-themed publish picker.
 *  • Shows existing Chapters (lessons) for a class.
 *  • Lets the teacher create a new Chapter inline.
 *  • Asks for a Topic headline + date for today's publish.
 *
 * Calls POST /studio/drafts/{draft_id}/publish-to-lesson with
 *   { class_id, lesson_id | lesson_title, topic_title, topic_date }.
 */
import React, { useEffect, useState } from 'react';
import { X, BookOpen, Plus, Check, CalendarDays, Loader2, Sparkles, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import AssignmentBlock from './AssignmentBlock';
import { notifyError } from '../lib/errors';

const API = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const token = localStorage.getItem('desky_token') || '';
  const res = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const t = await res.text();
  try { return JSON.parse(t); } catch { return { detail: t }; }
};

export default function PublishToLessonModal({ open, onClose, draft, className, classes = [], onPublished }) {
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [mode, setMode] = useState('pick'); // 'pick' | 'new'
  const [topicTitle, setTopicTitle] = useState('');
  const [topicDate, setTopicDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  // The class this session files into. Defaults to the class chosen on the
  // session card (draft.class_id), but stays editable here — the teacher can
  // switch to any other assigned class, which refreshes the chapter list.
  const [selectedClassId, setSelectedClassId] = useState(null);
  // Assigned classes flattened from the dept › batch › class tree, so each
  // option carries its batch name — same context the session cards show.
  const [fetchedClasses, setFetchedClasses] = useState([]);
  const propClassOptions = (classes || [])
    .map((c) => ({ id: c?.id || c?.class_id, name: c?.name || c?.class_name || c?.subject || 'Class' }))
    .filter((c) => c.id);
  // Prefer the grouped source (carries batch context); fall back to the flat prop.
  const classOptions = fetchedClasses.length ? fetchedClasses : propClassOptions;

  // ── Optional assignment that gets attached after the topic publishes ──
  const [asgnEnabled, setAsgnEnabled] = useState(false);
  const [asgnForm, setAsgnForm] = useState({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '23:59',
    points: 10,
  });

  // Reset the modal each time it opens.
  useEffect(() => {
    if (!open) return;
    setTopicTitle(draft?.title || 'Today\'s topic');
    setTopicDate(new Date().toISOString().slice(0, 10));
    setSelectedLessonId(null);
    setNewChapterTitle('');
    setSelectedClassId(draft?.class_id || null);
    setFetchedClasses([]);
    // Reset assignment slot each open. Default due date = +7 days.
    setAsgnEnabled(false);
    const inOneWeek = new Date(); inOneWeek.setDate(inOneWeek.getDate() + 7);
    setAsgnForm({
      title: '', description: '', dueDate: inOneWeek.toISOString().slice(0, 10),
      dueTime: '23:59', points: 10,
    });
  }, [open, draft?.class_id, draft?.title]);

  // Load the teacher's assigned classes grouped by dept › batch, so the picker
  // can show each class with its batch (matching the session cards) and the
  // teacher can switch class regardless of what the draft came in with.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const r = await api('/teacher/classes/grouped');
      if (!alive) return;
      const out = [];
      for (const g of r?.groups || []) {
        for (const b of g.batches || []) {
          for (const c of b.classes || []) {
            out.push({
              id: c.id,
              name: c.name,
              context: [b.batch?.name, g.department?.name].filter(Boolean).join(' · '),
            });
          }
        }
      }
      setFetchedClasses(out);
    })();
    return () => { alive = false; };
  }, [open]);

  // Load chapters whenever the target class changes (inherited or picked here).
  useEffect(() => {
    if (!open) return;
    if (!selectedClassId) { setChapters([]); setSelectedLessonId(null); setLoading(false); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await api(`/teacher/classes/${selectedClassId}/lessons`);
      if (!alive) return;
      const list = r.lessons || [];
      setChapters(list);
      if (list.length > 0) {
        setMode('pick');
        // Default to the chapter the teacher chose on the session card, so the
        // topic files into the right chapter; fall back to the newest chapter.
        const preselect = (draft?.lesson_id && list.some((c) => c.id === draft.lesson_id))
          ? draft.lesson_id
          : list[0].id;
        setSelectedLessonId(preselect);
      } else {
        setMode('new');
        setSelectedLessonId(null);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, selectedClassId, draft?.lesson_id]);

  if (!open) return null;

  const selectedClassName = classOptions.find((c) => c.id === selectedClassId)?.name
    || className || draft?.detected_class_name || 'class';

  const canSubmit = (() => {
    if (!selectedClassId) return false;
    if (!topicTitle.trim()) return false;
    if (mode === 'pick' && !selectedLessonId) return false;
    if (mode === 'new' && !newChapterTitle.trim()) return false;
    // If assignment is enabled, require title + due date
    if (asgnEnabled && (!asgnForm.title.trim() || !asgnForm.dueDate)) return false;
    return true;
  })();

  const handlePublish = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    const body = {
      class_id: selectedClassId,
      topic_title: topicTitle.trim(),
      topic_date: topicDate,
    };
    if (mode === 'pick') body.lesson_id = selectedLessonId;
    else body.lesson_title = newChapterTitle.trim();

    try {
      const res = await api(`/teacher/studio/drafts/${draft.id}/publish-to-lesson`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (res.published) {
        // Attach an assignment if the teacher opted in
        if (asgnEnabled && res.topic_id) {
          try {
            const aRes = await api('/teacher/assignments', {
              method: 'POST',
              body: JSON.stringify({
                topic_id: res.topic_id,
                title: asgnForm.title.trim(),
                description: asgnForm.description.trim(),
                due_date: asgnForm.dueDate,
                due_time: asgnForm.dueTime,
                points: asgnForm.points,
              }),
            });
            if (aRes?.assignment) {
              toast.success(`Published + assignment "${asgnForm.title}" attached`);
            } else {
              toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
              notifyError(aRes, 'Topic published, but assignment failed to save.');
            }
          } catch {
            toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
            toast.error('Topic published, but assignment failed to save.');
          }
        } else {
          toast.success(`Published "${res.topic_title}" to ${res.class_name}`);
        }
        onPublished && onPublished(res);
        onClose();
      } else {
        notifyError(res, 'Publish failed');
      }
    } catch (e) {
      toast.error('Network error — please retry');
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="publish-to-lesson-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — Deskey themed */}
        <div className="relative px-7 py-5 border-b border-zinc-100 bg-gradient-to-br from-[#34A853]/8 via-white to-[#06B6D4]/6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-[#34A853] to-[#0d9488] shadow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#34A853]">Publish session</p>
                <h2 className="text-xl font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  File today's topic in {selectedClassName}
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5">Choose the class and chapter — your topic goes under it, dated.</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100" data-testid="publish-modal-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
          {/* Class picker — always editable, defaulting to the class chosen on
              the session card. Switching class refreshes the chapter list. */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5 mb-3"><GraduationCap className="w-3.5 h-3.5 text-[#34A853]" /> Class</p>
            {classOptions.length === 0 ? (
              <div className="py-4 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">No classes assigned yet.</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="publish-class-picker">
                {classOptions.map((c) => {
                  const active = selectedClassId === c.id;
                  return (
                    <button key={c.id} onClick={() => setSelectedClassId(c.id)}
                      className={`text-left px-3 py-2 rounded-lg border transition-all ${active ? 'border-[#34A853] bg-[#34A853]/8 ring-1 ring-[#34A853]/20' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
                      data-testid={`publish-pick-class-${c.id}`}>
                      <span className={`block text-[12px] font-semibold ${active ? 'text-gray-900' : 'text-gray-600'}`}>{c.name}</span>
                      {c.context && (
                        <span className="block text-[10px] font-medium text-gray-400 mt-0.5">{c.context}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Chapter picker */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5 text-[#34A853]" /> Chapter</p>
              <div className="flex items-center gap-1 rounded-lg bg-zinc-100 p-0.5">
                <button
                  onClick={() => setMode('pick')}
                  disabled={chapters.length === 0}
                  className={`text-[11px] font-semibold px-3 py-1 rounded-md transition-colors ${mode === 'pick' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                  data-testid="publish-mode-pick"
                >Pick existing</button>
                <button
                  onClick={() => setMode('new')}
                  className={`text-[11px] font-semibold px-3 py-1 rounded-md transition-colors ${mode === 'new' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid="publish-mode-new"
                >+ New chapter</button>
              </div>
            </div>

            {/* Always-visible confirmation of the chapter this session will file into,
                so the chapter chosen on the session card is obvious (not just the class). */}
            {mode === 'pick' && selectedLessonId && (
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-[#34A853]/8 border border-[#34A853]/25 px-3 py-2" data-testid="publish-selected-chapter">
                <Check className="w-4 h-4 text-[#34A853] flex-shrink-0" />
                <p className="text-[12px] text-gray-700 min-w-0">
                  Filing under <span className="font-bold text-gray-900">{chapters.find(c => c.id === selectedLessonId)?.title || 'selected chapter'}</span>
                  {draft?.lesson_id && draft.lesson_id === selectedLessonId && (
                    <span className="text-[#0d9488] font-semibold"> · chosen on the session card</span>
                  )}
                </p>
              </div>
            )}

            {!selectedClassId ? (
              <div className="py-6 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">Choose a class above to see its chapters.</div>
            ) : mode === 'pick' ? (
              loading ? (
                <div className="py-8 text-center text-[12px] text-gray-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading chapters…</div>
              ) : chapters.length === 0 ? (
                <div className="py-6 text-center text-[12px] text-gray-500 bg-zinc-50 rounded-xl border border-dashed border-zinc-200">
                  No chapters yet — switch to <span className="font-semibold">+ New chapter</span>.
                </div>
              ) : (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                  {chapters.map(ch => {
                    const active = selectedLessonId === ch.id;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => setSelectedLessonId(ch.id)}
                        className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${active ? 'border-[#34A853] bg-[#34A853]/5 ring-1 ring-[#34A853]/20' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
                        data-testid={`publish-pick-chapter-${ch.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#34A853] text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                          {active ? <Check className="w-4 h-4" /> : <BookOpen className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-semibold text-gray-900 truncate">{ch.title}</p>
                          <p className="text-[11px] text-gray-500">
                            {ch.topic_count || 0} topic{(ch.topic_count || 0) !== 1 ? 's' : ''}
                            {ch.latest_topic_date && ` · last on ${new Date(ch.latest_topic_date).toLocaleDateString()}`}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="rounded-xl border border-[#34A853]/30 bg-[#34A853]/5 p-4">
                <label className="text-[10px] font-bold uppercase tracking-wider text-[#0d9488] mb-1.5 block">New chapter name</label>
                <input
                  value={newChapterTitle}
                  onChange={(e) => setNewChapterTitle(e.target.value)}
                  placeholder="e.g. Chapter 4 — Thermodynamics"
                  className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40 bg-white"
                  data-testid="publish-new-chapter-title"
                  autoFocus
                />
                <p className="text-[11px] text-gray-500 mt-2">Chapters group your daily topics. You can rename / reorder later.</p>
              </div>
            )}
          </div>

          {/* Topic details */}
          <div className="rounded-xl border border-zinc-200 p-4 bg-white">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-1.5"><Plus className="w-3.5 h-3.5 text-[#34A853]" /> Today's topic</p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1 block">Topic headline</label>
                <input
                  value={topicTitle}
                  onChange={(e) => setTopicTitle(e.target.value)}
                  placeholder="What did you cover today?"
                  className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40"
                  data-testid="publish-topic-title"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1 block flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date</label>
                <input
                  type="date"
                  value={topicDate}
                  onChange={(e) => setTopicDate(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-zinc-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]/40"
                  data-testid="publish-topic-date"
                />
              </div>
            </div>
          </div>

          {/* Optional assignment attachment */}
          <AssignmentBlock
            enabled={asgnEnabled}
            onToggle={setAsgnEnabled}
            title={asgnForm.title}
            description={asgnForm.description}
            dueDate={asgnForm.dueDate}
            dueTime={asgnForm.dueTime}
            points={asgnForm.points}
            onChange={(patch) => setAsgnForm(prev => ({ ...prev, ...patch }))}
          />
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-zinc-100 bg-gray-50/60 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[12px] font-semibold text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100" data-testid="publish-cancel">Cancel</button>
          <button
            onClick={handlePublish}
            disabled={!canSubmit || submitting}
            className="text-[12px] font-bold text-white bg-[#34A853] hover:bg-[#2d9249] disabled:opacity-40 px-5 py-2 rounded-lg flex items-center gap-2 shadow-sm transition-colors"
            data-testid="publish-confirm"
          >
            {submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publishing…</> : <>Publish topic</>}
          </button>
        </div>
      </div>
    </div>
  );
}
